/**
 * Manager Online category code → human Thai label.
 *
 * The upstream q.php / aspx endpoints expose a `category` field as a small
 * numeric code (e.g. "4", "26", "86"). Without a lookup the inbox UI shows
 * the raw digits, which is useless to editors. We vendor a snapshot of
 * mgronline's own browse.txt registry (1410 entries) so resolution happens
 * synchronously inside the worker — no extra fetch per item, no KV cache to
 * invalidate.
 *
 * Source: `https://w3.mgronline.com/rss/browse.txt` (PHP source file).
 * Refresh: re-run the parsing snippet in `packages/shared/scripts/gen-mgr-categories.ts`
 * (or manually with the inline node one-liner stashed in the commit
 * message of the migration that introduced this).
 */

import mgrCategoriesData from '../data/mgronline-categories.json';

const MGR_CATEGORY_MAP = mgrCategoriesData as Record<string, string>;

/**
 * Look up the human Thai label for a Manager Online category code.
 * Returns null when the code is empty/null/unknown so callers can decide
 * the fallback (show raw code, hide badge, etc.). Trims + ignores common
 * whitespace so an upstream field with surrounding spaces still resolves.
 */
export function resolveMgrCategoryName(code: string | null | undefined): string | null {
  if (!code) return null;
  const trimmed = code.trim();
  if (!trimmed) return null;
  return MGR_CATEGORY_MAP[trimmed] ?? null;
}

/** Total number of code → name entries in the vendored map. Exposed mostly
 *  for tests / health checks; not used by runtime callers. */
export const MGR_CATEGORY_ENTRY_COUNT = Object.keys(MGR_CATEGORY_MAP).length;
